/**
 * «Настройки» (Settings, ui-ux §6.24; GDD §4.5 `app/settings`, `#/settings`). Owner: UI/Visual (F3).
 *
 * Phone: a pushed page — top bar «‹ Настройки», then Rubricated section panels; the first is «Текст» (size, font and a
 * live preview line at the reading size) so a player who struggles to read finds «Простой» without scrolling (R48).
 * Desktop: an open folio with the categories on the left and one section on the right. Every change applies live
 * through the {@link SettingsPort}; there is no "Apply" button.
 */
import { signal, type ReadonlySignal, type Screen, type ScreenContext, type ScreenDef, type ScreenParams } from '../../shared';
import { disposeTree, h, type Child } from '../dom';
import { installKit } from '../kit/install';
import { button, rulerSlider, sealToggle, segmented } from '../components/controls';
import { panel, topBar } from '../components/surfaces';
import { confirmSheet, toast } from '../components/overlays';
import { glyph } from '../icons/shell.svg';
import { artImage } from '../art/ArtImage';
import { hapticsSupported, setHapticsEnabled } from '../feedback/haptics';
import { TEXT_SCALES, localSettingsPort, type SettingsPort, type UiSettings } from '../settings/model';
import { ut } from '../strings';
import '../styles/screens.css';

type SectionId = 'text' | 'image' | 'sound' | 'data' | 'keys' | 'about';
// Saved settings retain future fields; the player sees only controls used by the current game.
const SECTIONS: SectionId[] = ['text', 'image', 'sound', 'data', 'keys', 'about'];

const view = <T>(sig: ReadonlySignal<UiSettings>, pick: (s: UiSettings) => T): ReadonlySignal<T> => ({
  get: () => pick(sig.get()),
  subscribe: (fn) => { let last = pick(sig.get()); return sig.subscribe((s) => { const v = pick(s); if (v !== last) { last = v; fn(v); } }); },
});

function row(title: string, control: Child, hint?: string, stack = false): HTMLElement {
  return h('div', { class: `mc-row${stack ? ' mc-row--stack' : ''}` },
    h('div', { class: 'mc-row__label' }, h('span', { class: 'mc-row__title' }, title), hint ? h('span', { class: 'mc-row__hint' }, hint) : null),
    control);
}

function select<T extends string | number>(o: { label: string; value: ReadonlySignal<T>; options: Array<{ v: T; label: string }>; onChange: (v: T) => void; disabled?: string }): HTMLSelectElement {
  const el = h('select', {
    class: 'mc-select', attrs: { 'aria-label': o.label, ...(o.disabled ? { disabled: true, title: o.disabled } : {}) },
    on: { change: (e) => { const i = (e.target as HTMLSelectElement).selectedIndex; o.onChange(o.options[i].v); } },
  }, o.options.map((op) => h('option', { attrs: { value: String(op.v) } }, op.label)));
  const sync = (v: T): void => { el.selectedIndex = Math.max(0, o.options.findIndex((op) => op.v === v)); };
  sync(o.value.get());
  o.value.subscribe(sync);
  return el;
}

/** Builds every section's body against a port. */
function sections(port: SettingsPort, t: (k: string, p?: Record<string, string | number>) => string): Record<SectionId, () => Child> {
  const s = port.signal;
  const set = (patch: Partial<UiSettings>): void => port.set(patch);
  const toggle = (key: keyof UiSettings, title: string, hint?: string, locked?: string): HTMLElement =>
    row(title, sealToggle({ checked: view(s, (x) => Boolean(x[key])), label: title, onChange: (v) => set({ [key]: v } as Partial<UiSettings>), disabledReason: locked }), locked ?? hint);
  const env = port.env ?? {};
  const action = (key: keyof NonNullable<SettingsPort['actions']>) => async (): Promise<void> => {
    const fn = port.actions?.[key];
    if (!fn) { toast(t('ui.settings.unavailable')); return; }
    await fn();
  };
  const link = (label: string, onClick: () => void, danger = false): HTMLElement =>
    h('button', { class: `mc-row mc-row--link mc-row--button${danger ? ' is-danger' : ''}`, attrs: { type: 'button' }, on: { click: onClick } },
      h('span', { class: 'mc-row__label' }, h('span', { class: 'mc-row__title' }, label)), glyph('chevron-right', { size: 20 }));

  return {
    text: () => [
      row(t('ui.settings.language'), select({ label: t('ui.settings.language'), value: view(s, (x) => x.lang),
        options: [{ v: 'ru' as const, label: 'Русский' }, { v: 'en' as const, label: 'English' }], onChange: (lang) => set({ lang }) }), t('ui.settings.language.hint')),
      row(t('ui.settings.size'), segmented({
        label: t('ui.settings.size'), boxed: true, value: view(s, (x) => String(x.textScale)),
        items: TEXT_SCALES.map((v) => ({ id: String(v), label: `${v}` })), onChange: (id) => set({ textScale: Number(id) as UiSettings['textScale'] }),
      }), undefined, true),
      row(t('ui.settings.font'), segmented({
        label: t('ui.settings.font'), boxed: true, value: view(s, (x) => x.font),
        items: [{ id: 'calligraphic', label: t('ui.settings.font.calligraphic') }, { id: 'plain', label: t('ui.settings.font.plain') }],
        onChange: (id) => set({ font: id as UiSettings['font'] }),
      }), undefined, true),
      h('p', { class: 'mc-read mc-settings__preview' }, `«${t('ui.settings.preview')}»`),
    ],
    image: () => [
      toggle('lite', t('ui.settings.lite'), t('ui.settings.lite.hint'), env.webgpu === false ? t('ui.settings.lite.locked') : undefined),
      row(t('ui.settings.quality'), select({
        label: t('ui.settings.quality'), value: view(s, (x) => x.quality),
        options: (['auto', 'low', 'med', 'high', 'ultra'] as const).map((v) => ({ v, label: t(`ui.settings.quality.${v}`) })), onChange: (v) => set({ quality: v }),
      }), t('ui.settings.quality.hint')),
      row(t('ui.settings.fps'), segmented({ label: t('ui.settings.fps'), boxed: true, value: view(s, (x) => String(x.fps)), items: [{ id: '60', label: '60' }, { id: '30', label: '30' }], onChange: (id) => set({ fps: Number(id) as 60 | 30 }) })),
      row(t('ui.settings.motion'), segmented({
        label: t('ui.settings.motion'), boxed: true, value: view(s, (x) => x.motion),
        items: (['full', 'reduced', 'min'] as const).map((v) => ({ id: v, label: t(`ui.settings.motion.${v}`) })), onChange: (id) => set({ motion: id as UiSettings['motion'] }),
      }), undefined, true),
      toggle('shake', t('ui.settings.shake')),
      toggle('flash', t('ui.settings.flash')),
      env.fullscreen ? row(t('ui.settings.fullscreen'), sealToggle({
        checked: !!document.fullscreenElement, label: t('ui.settings.fullscreen'),
        onChange: (v) => { if (v) void document.documentElement.requestFullscreen?.(); else void document.exitFullscreen?.(); },
      })) : null,
    ],
    sound: () => [
      rulerSlider({ label: t('ui.settings.master'), min: 0, max: 100, step: 5, value: Math.round(s.get().master * 100), onInput: (v) => set({ master: v / 100 }) }),
      rulerSlider({ label: t('ui.settings.sfx'), min: 0, max: 100, step: 5, value: Math.round(s.get().sfx * 100), onInput: (v) => set({ sfx: v / 100 }) }),
      toggle('mute', t('ui.settings.mute')),
      env.vibrate ?? hapticsSupported() ? toggle('haptics', t('ui.settings.vibration')) : null,
    ],
    data: () => [
      env.standalone || !port.actions?.install ? null : link(t('ui.settings.install'), () => void action('install')()),
      link(t('ui.settings.exportSave'), () => void action('exportSave')()),
      link(t('ui.settings.importSave'), () => void action('importSave')()),
      link(t('ui.settings.diagnostics'), () => void action('copyDiagnostics')()),
      h('div', { class: 'mc-settings__danger' }, button({
        variant: 'danger', label: t('ui.settings.reset'), size: 'block',
        onClick: () => confirmSheet({
          title: t('ui.settings.reset.title'), consequence: t('ui.settings.reset.consequence'), verb: t('ui.settings.reset.verb'),
          typedWord: t('ui.settings.reset.word'), onConfirm: () => void action('resetProgress')(),
        }).open(),
      })),
    ],
    keys: () => (['everywhere', 'life', 'fight', 'road', 'make', 'cards'] as const).map((k) => h('p', { class: 'mc-settings__keys' }, t(`ui.settings.keys.${k}`))),
    about: () => [
      h('p', { class: 'mc-read' }, t('ui.settings.about.lead')),
      h('p', { class: 'mc-caption' }, t('ui.settings.about.version', { version: String(import.meta.env?.MODE ?? 'dev') })),
      h('p', { class: 'mc-caption' }, t('ui.settings.about.fonts')),
    ],
  };
}

/** The Settings `ScreenDef`. `portOf(ctx)` lets F1 pass its own settings store; the default is a local port. */
export function createSettingsScreen(portOf?: (ctx: ScreenContext) => SettingsPort): ScreenDef {
  return {
    id: 'app/settings', route: '#/settings', aliases: ['settings'], titleKey: 'app.screen.settings', needs3d: false, kind: 'page', scope: 'P0',
    parseParams: (q: URLSearchParams): ScreenParams => ({ section: q.get('section') ?? undefined }),
    create(ctx: ScreenContext, params: ScreenParams): Screen {
      void installKit();
      const port = portOf?.(ctx) ?? localSettingsPort();
      setHapticsEnabled(port.get().haptics);
      const unsubH = port.signal.subscribe((v) => setHapticsEnabled(v.haptics));
      const t = ctx.t ?? ut;
      const bodies = sections(port, t);
      const desk = matchMedia('(min-width: 1024px) and (min-height: 521px)');
      const active = signal<SectionId>((SECTIONS as string[]).includes(String(params.section)) ? params.section as SectionId : 'text');

      const back = button({ variant: 'quill', icon: 'back', ariaLabel: t('ui.kit.back'), onClick: () => ctx.router.back() });
      const head = topBar({ title: t('ui.settings.title'), start: back });

      const phoneList = h('div', { class: 'mc-settings__list' },
        SECTIONS.filter((id) => id !== 'keys' || desk.matches).map((id, i) => panel({ tier: 'rubric', title: t(`ui.settings.sec.${id}`), titleAs: 'h2', lombard: i === 0, flourish: i === 0, penwork: i % 2 ? 'alt' : 'red', attrs: { 'data-section': id } }, bodies[id]())));

      const nav = h('nav', { class: 'mc-settings__nav', attrs: { 'aria-label': t('ui.settings.sections') } },
        SECTIONS.map((id) => h('button', {
          class: 'mc-settings__navitem', attrs: { type: 'button', 'aria-current': { get: () => (active.get() === id ? 'page' : 'false'), subscribe: (fn: (v: string) => void) => active.subscribe((v) => fn(v === id ? 'page' : 'false')) } },
          on: { click: () => active.set(id) },
        }, t(`ui.settings.sec.${id}`))),
        artImage('ui/drolleries/margin_snail_knight', { size: 120, emblem: 'snail', tone: 'gold', class: 'mc-settings__drollery' }));
      const pane = h('div', { class: 'mc-settings__pane' });
      const renderPane = (id: SectionId): void => {
        disposeTree(pane);
        pane.replaceChildren(panel({ tier: 'rubric', title: t(`ui.settings.sec.${id}`), titleAs: 'h2', lombard: true, flourish: true }, bodies[id]()));
      };
      renderPane(active.get());
      const unsubA = active.subscribe(renderPane);
      const folio = h('div', { class: 'mc-settings__folio mc-frame9', attrs: { 'data-frame': 'gilt', 'data-orn': 'frame' } },
        h('h1', { class: 'mc-settings__deskTitle' }, t('ui.settings.title')),
        h('div', { class: 'mc-settings__spread' }, nav, pane));

      const root = h('section', { class: 'mc-screen mc-settings', attrs: { 'data-screen': 'app/settings', 'aria-labelledby': 'mc-settings-h' } },
        h('h1', { class: 'sr-only', attrs: { id: 'mc-settings-h' } }, t('ui.settings.title')),
        head, phoneList, folio);
      return {
        root,
        isIdle: () => true,
        debugState: () => ({ settings: port.get(), section: active.get() }),
        onBack: () => false,
        dispose: () => { unsubH(); unsubA(); disposeTree(root); },
      };
    },
  };
}

/** The default registration (F1 swaps the `app/settings` stub for this, or for `createSettingsScreen(itsPort)`). */
export const settingsScreen: ScreenDef = createSettingsScreen();
